import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Car, ChevronRight, ChevronLeft, Search, Flame, History } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GlassCard, EmptyState } from "@/components/erp";
import { UrunKart } from "@/components/bayi/BayiUrunler";
import { useAuth } from "@/context/AuthContext";
import { katalog, katalogYenile, katalogAra, aracAgaci, agHatasi, useCevrimici } from "@/lib/katalog";
import { eslesir, normTr, sonSecilenler, sonGetir, sonaEkle } from "@/lib/ara";
import api from "@/lib/api";
import { useMarkaLogolari, logoUrl } from "@/lib/markaLogo";

const tid = (s) => String(s).replace(/\s+/g, "-");

const Kart = ({ testid, baslik, alt, onClick, logo }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className="group text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 p-4 flex items-center gap-3 transition-colors">
    {logo ? <img src={logo} alt={baslik} loading="lazy" className="w-11 h-11 shrink-0 rounded-xl object-contain bg-white p-1" data-testid={`${testid}-logo`} />
      : <span className="w-11 h-11 shrink-0 rounded-xl grad-bg flex items-center justify-center font-head font-bold text-white">{baslik.charAt(0)}</span>}
    <span className="min-w-0 flex-1"><span className="block font-medium truncate">{baslik}</span><span className="block text-xs text-muted-foreground">{alt}</span></span>
    <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
  </button>
);

const Yol = ({ marka, model, sifirla, markaya }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-sm" data-testid="arac-yol">
    <button data-testid="arac-yol-markalar" onClick={sifirla} className="text-muted-foreground hover:text-white">Markalar</button>
    {marka && <><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /><button data-testid="arac-yol-marka" onClick={markaya} className={model ? "text-muted-foreground hover:text-white" : "font-medium"}>{marka}</button></>}
    {model && <><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /><span className="font-medium" data-testid="arac-yol-model">{model}</span></>}
  </div>
);

const MarkaAdim = ({ agac, onSec }) => {
  const logolar = useMarkaLogolari();
  const [q, setQ] = useState("");
  const [harf, setHarf] = useState("");
  const harfler = useMemo(() => [...new Set(agac.map((b) => normTr(b.marka).charAt(0).toUpperCase()))].sort(), [agac]);
  const l = agac.filter((b) => eslesir(q, b.marka) && (!harf || normTr(b.marka).toUpperCase().startsWith(harf)));
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative sm:w-72"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="arac-marka-ara" placeholder="Marka ara…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white/5" /></div>
        <div className="flex flex-wrap gap-1">
          {["", ...harfler].map((h) => (
            <button key={h || "tum"} data-testid={`arac-harf-${h || "tumu"}`} onClick={() => setHarf(h)}
              className={`min-w-8 h-8 px-2 rounded-lg text-xs transition-colors ${harf === h ? "grad-bg text-white" : "bg-white/5 hover:bg-white/10"}`}>{h || "Tümü"}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {l.map((b) => <Kart key={b.marka} testid={`arac-marka-${tid(b.marka)}`} baslik={b.marka} logo={logoUrl(logolar, b.marka)} alt={`${b.adet} ürün · ${b.modeller.length} model`} onClick={() => onSec(b)} />)}
      </div>
      {!l.length && <EmptyState text="Aramanıza uygun marka yok." testid="arac-marka-bos" />}
    </div>
  );
};

const ModelAdim = ({ b, onSec }) => {
  const logolar = useMarkaLogolari();
  return (
  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
    <Kart testid="arac-model-tumu" baslik={`Tüm ${b.marka}`} logo={logoUrl(logolar, b.marka)} alt={`${b.adet} ürün`} onClick={() => onSec(null)} />
    {b.modeller.map((m) => (
      <Kart key={m.model} testid={`arac-model-${tid(m.model)}`} baslik={m.model} alt={`${m.adet} ürün${m.yillar.length ? ` · ${m.yillar[m.yillar.length - 1]}–${m.yillar[0]}` : ""}`} onClick={() => onSec(m)} />
    ))}
  </div>
  );
};

const Sonuclar = ({ uid, pb, onEkle, marka, model }) => {
  const [yil, setYil] = useState(null);
  const [sayfa, setSayfa] = useState(1);
  const [d, setD] = useState(null);
  const cevrimici = useCevrimici();
  useEffect(() => {
    if (model) { sonaEkle(uid, "bayi-arac", { marka: marka.marka, model: model.model }, (a, b) => normTr(`${a.marka}|${a.model}`) === normTr(`${b.marka}|${b.model}`)); }
  }, [uid, marka, model]);
  useEffect(() => { setSayfa(1); }, [yil]);
  useEffect(() => {
    let iptal = false;
    const p = { marka: marka.marka, model: model?.model || "", yil: yil || undefined, sayfa };
    api.get("/bayi/urunler", { params: p }).then((r) => { if (!iptal) setD(r.data); })
      .catch((e) => { const y = agHatasi(e) && katalogAra(uid, { ...p, yil }); if (y && !iptal) setD(y); });
    return () => { iptal = true; };
  }, [marka, model, yil, sayfa, uid, cevrimici]);
  const son = d ? Math.max(1, Math.ceil(d.toplam / d.sayfa_boyu)) : 1;
  return (
    <div className="space-y-4" data-testid="arac-sonuclar">
      {model?.yillar.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="arac-yillar">
          {[null, ...model.yillar].map((y) => (
            <button key={y || "tum"} data-testid={`arac-yil-${y || "tumu"}`} onClick={() => setYil(y)}
              className={`h-8 px-3 rounded-full text-xs transition-colors ${yil === y ? "grad-bg text-white" : "bg-white/5 hover:bg-white/10"}`}>{y || "Tüm yıllar"}</button>
          ))}
        </div>
      )}
      {d && <div className="text-xs text-muted-foreground" data-testid="arac-sonuc-sayi">{[marka.marka, model?.model, yil].filter(Boolean).join(" ")} için {d.toplam} ürün</div>}
      {d && !d.urunler.length && <EmptyState text="Bu araca uygun ürün bulunamadı." testid="arac-sonuc-bos" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-3">{d?.urunler.map((u) => <UrunKart key={u.id} u={u} pb={pb} onEkle={onEkle} q={model?.model || ""} cevrimdisi={!!d.cevrimdisi} />)}</div>
      {d && d.toplam > d.sayfa_boyu && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button data-testid="arac-onceki" disabled={sayfa <= 1} onClick={() => setSayfa(sayfa - 1)} className="p-2 rounded-full bg-white/5 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-muted-foreground">{sayfa} / {son}</span>
          <button data-testid="arac-sonraki" disabled={sayfa >= son} onClick={() => setSayfa(sayfa + 1)} className="p-2 rounded-full bg-white/5 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

const Populer = ({ agac, onMarka, onModel }) => {
  const { user } = useAuth();
  const logolar = useMarkaLogolari();
  const [p, setP] = useState(null);
  const [son, setSon] = useState(() => sonSecilenler(user.id, "bayi-arac"));
  useEffect(() => {
    api.get("/bayi/populer-araclar").then((r) => setP(r.data)).catch(() => {});
    sonGetir(user.id, "bayi-arac").then(setSon);
  }, [user.id]);
  const bul = (m) => agac.find((b) => normTr(b.marka) === normTr(m));
  const eslestir = (x) => { const b = bul(x.marka); const md = b?.modeller.find((y) => normTr(y.model) === normTr(x.model)); return md && { b, md, adet: x.adet }; };
  const sonlar = son.map(eslestir).filter(Boolean).slice(0, 3);
  const sonA = new Set(sonlar.map((x) => normTr(`${x.b.marka}|${x.md.model}`)));
  const modeller = (p?.modeller || []).map(eslestir).filter((x) => x && !sonA.has(normTr(`${x.b.marka}|${x.md.model}`)));
  const markalar = (p?.markalar || []).map((x) => ({ b: bul(x.marka), adet: x.adet })).filter((x) => x.b);
  if (!modeller.length && !markalar.length && !sonlar.length) return null;
  const cip = "shrink-0 whitespace-nowrap inline-flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/10 hover:border-white/20 text-sm transition-colors active:scale-95";
  const ikon = (m) => (logoUrl(logolar, m) ? <img src={logoUrl(logolar, m)} alt="" className="w-7 h-7 rounded-full object-contain bg-white p-0.5" />
    : <span className="w-7 h-7 rounded-full grad-bg flex items-center justify-center text-[11px] font-bold text-white">{m.charAt(0)}</span>);
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="arac-populer">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Flame className="w-3.5 h-3.5 text-orange-300" />
        {[sonlar.length ? "Son baktıklarınız" : "", (p?.modeller?.length || p?.markalar?.length) ? (p.kaynak === "kendi" ? "en çok sipariş verdikleriniz" : "bayilerin en çok sipariş verdikleri") : ""].filter(Boolean).join(" ve ").replace(/^./, (c) => c.toLocaleUpperCase("tr"))}</div>
      {(sonlar.length > 0 || modeller.length > 0) && <div className="flex gap-2 overflow-x-auto sm:flex-wrap pb-1 erp-scroll">{sonlar.map(({ b, md }) => (
        <button key={`son-${b.marka}-${md.model}`} data-testid={`arac-son-${tid(b.marka)}-${tid(md.model)}`} onClick={() => onModel(b, md)} className={`${cip} border-sky-400/30 bg-sky-400/10`} title="Son baktığınız araç">
          <History className="w-4 h-4 text-sky-300 ml-1.5" />{ikon(b.marka)}<span className="font-medium">{b.marka}</span><span className="text-muted-foreground">{md.model}</span></button>
      ))}{modeller.map(({ b, md, adet }) => (
        <button key={`${b.marka}-${md.model}`} data-testid={`arac-populer-model-${tid(b.marka)}-${tid(md.model)}`} onClick={() => onModel(b, md)} className={cip} title={`${adet} sipariş`}>
          {ikon(b.marka)}<span className="font-medium">{b.marka}</span><span className="text-muted-foreground">{md.model}</span></button>
      ))}</div>}
      {markalar.length > 0 && <div className="flex gap-2 overflow-x-auto sm:flex-wrap pb-1 erp-scroll">{markalar.map(({ b, adet }) => (
        <button key={b.marka} data-testid={`arac-populer-marka-${tid(b.marka)}`} onClick={() => onMarka(b)} className={cip} title={`${adet} sipariş`}>
          {ikon(b.marka)}<span>{b.marka}</span><span className="text-[10px] text-muted-foreground">tüm modeller</span></button>
      ))}</div>}
    </div>
  );
};

export const BayiAracSec = ({ pb, onEkle }) => {
  const { user } = useAuth();
  const [k, setK] = useState(() => katalog(user.id));
  const [marka, setMarka] = useState(null);
  const [model, setModel] = useState(undefined);
  useEffect(() => { katalogYenile(user.id).then(setK).catch(() => {}); }, [user.id]);
  const agac = useMemo(() => aracAgaci(k?.urunler || []), [k]);
  const ekle = (u, m) => { onEkle(u, m); toast.success(`${u.kod} sepete eklendi`); };
  return (
    <GlassCard className="p-5 space-y-5" data-testid="bayi-arac-sec">
      <div className="flex items-center gap-2 font-head font-semibold"><Car className="w-5 h-5" />Aracıma göre bul</div>
      <p className="text-xs text-muted-foreground -mt-3">Aracınızın markasını, sonra modelini seçin; uyumlu tüm ürünler listelenir.</p>
      <Yol marka={marka?.marka} model={model?.model} sifirla={() => { setMarka(null); setModel(undefined); }} markaya={() => setModel(undefined)} />
      {!k && <p className="text-sm text-muted-foreground" data-testid="arac-yukleniyor">Araç listesi yükleniyor…</p>}
      {k && !agac.length && <EmptyState text="Ürünlere henüz araç bilgisi girilmedi." testid="arac-bos" />}
      {k && agac.length > 0 && !marka && <Populer agac={agac} onMarka={(b) => { setMarka(b); setModel(undefined); }} onModel={(b, md) => { setMarka(b); setModel(md); }} />}
      {k && agac.length > 0 && !marka && <MarkaAdim agac={agac} onSec={(b) => { setMarka(b); setModel(undefined); }} />}
      {marka && model === undefined && <ModelAdim b={marka} onSec={setModel} />}
      {marka && model !== undefined && <Sonuclar key={`${marka.marka}-${model?.model || ""}`} uid={user.id} pb={pb} onEkle={ekle} marka={marka} model={model} />}
    </GlassCard>
  );
};
